/**
 * Global Country Configuration
 *
 * Single source of truth for all country-specific settings:
 * validation rules, phone codes, currencies, locales, etc.
 *
 * Used by:
 * - useCountryDetection hook
 * - Country-specific validation schemas
 * - Address form components
 * - Registration flow
 */

// ─── Upgrade-flow component keys ─────────────────────────────────────────────
/**
 * Each country can declare which variants of the upgrade-flow components to use.
 *
 * upgradeModalComponent   – the product-selection step (Step 0)
 * upgradeReviewComponent  – the review & confirm step   (Step 1)
 *
 * The string values are resolved by the consumer (e.g. InformationAlerts /
 * NoticeAlerts) to the actual React component via the UPGRADE_COMPONENT_MAP
 * exported below.
 *
 * Currently supported variants:
 *   'default'  – original Brazil / Japan flow  (UpgradeModal + UpgradeReviewModal)
 *   'kh'       – Cambodia flow                 (UpgradeModalKH + UpgradeReviewModalKH)
 *               → 10k / 30k / 50k tiers, USD currency, no payment-selection step
 */
export type UpgradeComponentVariant = 'default' | 'kh';

// ─── Hero-section component keys ─────────────────────────────────────────────
/**
 * Each country can declare which variant of the auth hero-section to render.
 *
 * 'default'  – images 1-7 slideshow (Brazil / Japan)
 * 'kh'       – images 1-6 slideshow, no loginImage7 (Cambodia)
 */
export type HeroSectionVariant = 'default' | 'kh';

export interface CountryConfig {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  phoneCode: string;
  currency: string;
  locale: string;
  postalCode: {
    format: string; // Regex pattern
    label: string; // Display label (e.g., "ZIP Code", "Postal Code", "CEP")
    placeholder: string;
    length: number | { min: number; max: number };
  };
  phoneNumber: {
    format: string;
    length: number | { min: number; max: number };
  };
  addressFieldOrder: string[];
  fixedCountryName: string; // For the disabled country field
  paymentMethods: Array<'pix' | 'creditCard' | 'bankTransfer'>;
  defaultPaymentMethod: 'pix' | 'creditCard';
  skipPaymentSelection: boolean;
  requireShippingAddress: boolean;
  enableAutoShipment: boolean;
  paymentUrl: string;
  /** Which upgrade-flow component variant to use for this country */
  upgradeComponentVariant: UpgradeComponentVariant;
  /** Which auth hero-section slideshow variant to use for this country */
  heroSectionVariant: HeroSectionVariant;
  pointsConfig: {
    membershipTiers: {
      basic: number;
      standard: number;
      max: number;
    };
    autoShipment: number;
  };
  costsConfig: {
    membershipCost: number;
    shippingCost: number;
    currency: string;
  };
}

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  BR: {
    code: 'BR',
    name: 'Brazil',
    phoneCode: '+55',
    currency: 'BRL',
    locale: 'pt-BR',
    postalCode: {
      format: '^\\d{8}$',
      label: 'CEP',
      placeholder: 'Postal code',
      length: 8,
    },
    phoneNumber: {
      format: '^\\d{10,11}$',
      length: { min: 10, max: 11 },
    },
    addressFieldOrder: ['streetAndNumber', 'city', 'state', 'postalCode', 'country'],
    fixedCountryName: 'Brazil',
    paymentMethods: ['pix', 'creditCard'],
    defaultPaymentMethod: 'pix',
    skipPaymentSelection: false,
    requireShippingAddress: true,
    enableAutoShipment: false,
    paymentUrl: '',
    upgradeComponentVariant: 'default',
    heroSectionVariant: 'default',
    pointsConfig: {
      membershipTiers: { basic: 20000, standard: 50000, max: 80000 },
      autoShipment: 10000,
    },
    costsConfig: {
      membershipCost: 3000,
      shippingCost: 6000,
      currency: 'JPY',
    },
  },
  JP: {
    code: 'JP',
    name: 'Japan',
    phoneCode: '+81',
    currency: 'JPY',
    locale: 'ja',
    postalCode: {
      format: '^\\d{7}$',
      label: 'Postal Code',
      placeholder: 'Postal code',
      length: 7,
    },
    phoneNumber: {
      format: '^\\d{10,11}$',
      length: { min: 10, max: 11 },
    },
    addressFieldOrder: ['postalCode', 'state', 'city', 'streetAndNumber', 'country'],
    fixedCountryName: 'Japan',
    paymentMethods: ['creditCard'],
    defaultPaymentMethod: 'creditCard',
    skipPaymentSelection: false,
    requireShippingAddress: false,
    enableAutoShipment: true,
    paymentUrl: '',
    upgradeComponentVariant: 'default',
    heroSectionVariant: 'default',
    pointsConfig: {
      membershipTiers: { basic: 20000, standard: 50000, max: 80000 },
      autoShipment: 10000,
    },
    costsConfig: {
      membershipCost: 3000,
      shippingCost: 6000,
      currency: 'JPY',
    },
  },
  KH: {
    code: 'KH',
    name: 'Cambodia',
    phoneCode: '+855',
    currency: 'USD',
    locale: 'en',
    postalCode: {
      format: '^\\d{5}$',
      label: 'ZIP Code',
      placeholder: 'Enter 5 digit ZIP code',
      length: 5,
    },
    phoneNumber: {
      format: '^\\d{9}$',
      length: 9,
    },
    addressFieldOrder: ['streetAndNumber', 'state', 'city', 'postalCode', 'country'],
    fixedCountryName: 'Cambodia',
    paymentMethods: ['creditCard'],
    defaultPaymentMethod: 'creditCard',
    skipPaymentSelection: true,
    requireShippingAddress: false,
    enableAutoShipment: true,
    paymentUrl: '',
    upgradeComponentVariant: 'kh',
    heroSectionVariant: 'kh',
    pointsConfig: {
      membershipTiers: { basic: 10000, standard: 30000, max: 50000 },
      autoShipment: 5000,
    },
    costsConfig: {
      membershipCost: 4000,
      shippingCost: 10000,
      currency: 'JPY',
    },
  },
};

// Default country code (Brazil)
export const DEFAULT_COUNTRY = 'BR';

// Language to country mapping
export const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  ja: 'JP',
  'pt-BR': 'BR',
  en: 'BR', // Default English to Brazil
};

// URL path to country code mapping
export const URL_COUNTRY_TO_CODE: Record<string, string> = {
  japan: 'JP',
  brazil: 'BR',
  cambodia: 'KH',
};

/**
 * Maps a country code to the upgrade-component variant key declared in its
 * CountryConfig.  Consumers resolve the returned key to the actual component
 * via a local lookup – keeping this file free of React / component imports.
 *
 * Usage example (in InformationAlerts / NoticeAlerts):
 *
 *   import { getUpgradeComponentVariant } from '../../../config/globalConfig';
 *   import UpgradeModal    from './UpgradeModal';
 *   import UpgradeModalKH  from './UpgradeModalKH';
 *
 *   const variant = getUpgradeComponentVariant(countryCode);
 *   const UpgradeComponent = variant === 'kh' ? UpgradeModalKH : UpgradeModal;
 */
export const getUpgradeComponentVariant = (countryCode: string): UpgradeComponentVariant => {
  const config = COUNTRY_CONFIGS[countryCode];
  return config?.upgradeComponentVariant ?? 'default';
};

/**
 * Maps a country code to the hero-section variant declared in its CountryConfig.
 *
 * Usage example (in AuthPageLayout):
 *
 *   import { getHeroSectionVariant } from '../../../config/globalConfig';
 *   import HeroSection         from '../../elements/authElements/HeroSection';
 *   import HeroSectionCambodia from '../../elements/authElements/HeroSectionCambodia';
 *
 *   const heroVariant = getHeroSectionVariant(countryCode);
 *   const HeroComponent = heroVariant === 'kh' ? HeroSectionCambodia : HeroSection;
 */
export const getHeroSectionVariant = (countryCode: string): HeroSectionVariant => {
  const config = COUNTRY_CONFIGS[countryCode];
  return config?.heroSectionVariant ?? 'default';
};

// ── Helper functions ──

export const getCountryConfig = (countryCode: string): CountryConfig | null => {
  return COUNTRY_CONFIGS[countryCode] || null;
};

export const getCountryConfigOrDefault = (countryCode: string): CountryConfig => {
  return COUNTRY_CONFIGS[countryCode] || COUNTRY_CONFIGS[DEFAULT_COUNTRY];
};

export const getCountryFromLanguage = (language: string): string => {
  return LANGUAGE_TO_COUNTRY[language] || DEFAULT_COUNTRY;
};

export const validatePostalCode = (
  postalCode: string,
  countryCode: string,
): boolean => {
  const config = getCountryConfig(countryCode);
  if (!config) return false;

  const cleaned = postalCode.replace(/\D/g, '');
  const lengthConfig = config.postalCode.length;

  if (typeof lengthConfig === 'number') {
    if (cleaned.length !== lengthConfig) return false;
  } else {
    if (cleaned.length < lengthConfig.min || cleaned.length > lengthConfig.max) return false;
  }

  const regex = new RegExp(config.postalCode.format);
  return regex.test(cleaned);
};

export const validatePhoneNumber = (
  phoneNumber: string,
  countryCode: string,
): boolean => {
  const config = getCountryConfig(countryCode);
  if (!config) return false;

  const cleaned = phoneNumber.replace(/\D/g, '');
  const lengthConfig = config.phoneNumber.length;

  if (typeof lengthConfig === 'number') {
    if (cleaned.length !== lengthConfig) return false;
  } else {
    if (cleaned.length < lengthConfig.min || cleaned.length > lengthConfig.max) return false;
  }

  const regex = new RegExp(config.phoneNumber.format);
  return regex.test(cleaned);
};

export const getPostalCodeMaxLength = (countryCode: string): number => {
  const config = getCountryConfig(countryCode);
  if (!config) return 10;
  const len = config.postalCode.length;
  return typeof len === 'number' ? len : len.max;
};

export const getPhoneNumberMaxLength = (countryCode: string): number => {
  const config = getCountryConfig(countryCode);
  if (!config) return 15;
  const len = config.phoneNumber.length;
  return typeof len === 'number' ? len : len.max;
};

export const formatPhoneWithCountryCode = (
  phoneNumber: string,
  countryCode: string,
): string => {
  const config = getCountryConfig(countryCode);
  if (!config) return phoneNumber;
  const cleaned = phoneNumber.replace(/\D/g, '');
  return `${config.phoneCode}${cleaned}`;
};

export const getPostalCodeLengthDescription = (countryCode: string): string => {
  const config = getCountryConfig(countryCode);
  if (!config) return '';
  const len = config.postalCode.length;
  return typeof len === 'number' ? String(len) : `${len.min}-${len.max}`;
};

export const getPhoneNumberLengthDescription = (countryCode: string): string => {
  const config = getCountryConfig(countryCode);
  if (!config) return '';
  const len = config.phoneNumber.length;
  return typeof len === 'number' ? String(len) : `${len.min}-${len.max}`;
};
